// Payment System v3.0 PR 7 — VFI inflow wizard (design spec §8.3).
// Route-driven page at /capital-markets/pools/:id/vfi/new. Two steps:
//   1. Inflow details — source, gross VFI, per-loan attribution table
//      (validated to sum to exactly 100%)
//   2. Preview — Layer-1 per-loan split + Layer-2 totals + WF2 disbursements
//
// The loan autocomplete is sourced from the pool's composition (the loans
// the pool actually holds); the user cannot attribute VFI to a loan that
// isn't in this pool.

import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Plus, Trash2 } from "lucide-react";
import { Button, Card, FormField, Input } from "@/design-system/components/ui";
import {
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { WaterfallBlockedNotice } from "@/components/payments/WaterfallBlockedNotice";
import { extractErrorMessage } from "@/api/client";
import { cn } from "@/design-system/lib/utils";
import {
  usePoolComposition,
  usePreviewVfi,
  useRecordVfi,
  useSpvPool,
} from "@/hooks/capitalMarkets/useSpvPools";
import type { VfiPreview } from "@/types/spvPool";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";
import { formatCurrency, formatPct } from "../shared/format";

type Step = 1 | 2;

interface AttributionRow {
  id: string;
  loanId: string;
  percent: string; // 0..100 raw input
}

function newRow(): AttributionRow {
  return { id: crypto.randomUUID(), loanId: "", percent: "" };
}

export function VfiInflowWizard() {
  const { id: poolId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const tz = useUserTimeZone();

  const poolQuery = useSpvPool(poolId);
  // Pool composition (Contributed-status rows) gives us the loans actually
  // held by the pool — the only loans that can receive VFI attribution.
  const compositionQuery = usePoolComposition(poolId, "Contributed");
  const preview = usePreviewVfi(poolId ?? "");
  const record = useRecordVfi(poolId ?? "");

  const [step, setStep] = useState<Step>(1);
  const [inflowDate, setInflowDate] = useState(
    () => isoDateInTz(new Date(), tz),
  );
  const [source, setSource] = useState("");
  const [grossVfi, setGrossVfi] = useState("");
  const [rows, setRows] = useState<AttributionRow[]>([newRow()]);
  const [previewData, setPreviewData] = useState<VfiPreview | null>(null);

  const pool = poolQuery.data;

  // Deduplicate composition rows by loanId — the composition table is
  // segment-grained but VFI attribution is loan-grained.
  const loans = useMemo(() => {
    const seen = new Set<string>();
    const out: { loanId: string }[] = [];
    for (const row of compositionQuery.data ?? []) {
      if (!seen.has(row.loanId)) {
        seen.add(row.loanId);
        out.push({ loanId: row.loanId });
      }
    }
    return out;
  }, [compositionQuery.data]);

  const totalPercent = useMemo(
    () =>
      rows.reduce((sum, r) => {
        const n = Number(r.percent);
        return Number.isFinite(n) ? sum + n : sum;
      }, 0),
    [rows],
  );
  // Allow a tiny rounding window — operators commonly enter 33.33/33.33/33.34.
  const percentValid = Math.abs(totalPercent - 100) < 0.01;

  const grossValid = Number.isFinite(Number(grossVfi)) && Number(grossVfi) > 0;
  const sourceValid = source.trim().length > 0;
  const rowsValid =
    rows.length > 0 &&
    rows.every((r) => r.loanId && Number(r.percent) > 0) &&
    new Set(rows.map((r) => r.loanId)).size === rows.length; // no dup loans

  const step1Valid = grossValid && sourceValid && rowsValid && percentValid;

  const handleAddRow = () => setRows((rs) => [...rs, newRow()]);
  const handleRemoveRow = (id: string) =>
    setRows((rs) => (rs.length === 1 ? rs : rs.filter((r) => r.id !== id)));
  const updateRow = (id: string, patch: Partial<AttributionRow>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const buildBody = () => ({
    inflowDate,
    source: source.trim(),
    grossVfi: Number(grossVfi),
    // Backend takes attributionPercent as a 0..1 fraction in the engine
    // contract; preview/execute accept 0..100 from the UI and the
    // controller normalizes. The IVfiInflowEngine doc says the set is
    // server-validated to sum to 1.0, so we pass the 0..1 form here.
    attributions: rows.map((r) => ({
      loanId: r.loanId,
      attributionPercent: Number(r.percent) / 100,
    })),
  });

  const handleRunPreview = () => {
    if (!poolId) return;
    preview
      .mutateAsync(buildBody())
      .then((data) => {
        setPreviewData(data);
        setStep(2);
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const handleConfirm = () => {
    if (!poolId) return;
    record
      .mutateAsync(buildBody())
      .then(() => {
        toast.success("VFI inflow recorded.");
        navigate(`/capital-markets/pools/${poolId}?tab=cashflow`);
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  if (poolQuery.isLoading || !pool) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading pool…</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex items-center gap-space-2">
        <button
          type="button"
          onClick={() =>
            navigate(`/capital-markets/pools/${pool.id}?tab=cashflow`)
          }
          className="text-gold-shadow hover:underline text-small flex items-center gap-space-1"
        >
          <ArrowLeft size={14} aria-hidden /> Back to pool
        </button>
      </div>
      <div className="flex flex-col gap-space-1">
        <h1 className="font-display font-bold text-h2 text-onyx m-0">
          Record VFI inflow — {pool.poolName}
        </h1>
        <p className="text-small text-gray-mid m-0">
          Layer 1 splits each attributed dollar into Phoenix-retained +
          pool-attributable. Layer 2 sends the pool-attributable through WF2
          starting at the 20% VMP admin fee.
        </p>
      </div>

      <Stepper step={step} />

      {step === 1 && (
        <Card>
          <h3 className="font-display font-bold text-h3 m-0">Inflow details</h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-3 mt-space-3">
            <FormField label="Inflow date" required>
              <Input
                type="date"
                value={inflowDate}
                onChange={(e) => setInflowDate(e.target.value)}
                aria-label="Inflow date"
              />
            </FormField>
            <FormField label="Source" required>
              <Input
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="e.g. Refi cure"
                aria-label="Source"
              />
            </FormField>
            <FormField label="Gross VFI (USD)" required>
              <Input
                type="number"
                min={0}
                step="any"
                value={grossVfi}
                onChange={(e) => setGrossVfi(e.target.value)}
                placeholder="0"
                aria-label="Gross VFI"
              />
            </FormField>
          </div>

          <h4 className="font-display font-bold text-onyx m-0 mt-space-4">
            Attribution
          </h4>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            Sum of attribution percentages must equal 100.00.
          </p>

          <div className="mt-space-3 overflow-x-auto">
            <table className="w-full text-small">
              <thead>
                <tr className="text-left text-gold uppercase text-[10px] font-display tracking-label">
                  <th className="py-space-2 pr-space-2 w-2/3">Loan</th>
                  <th className="py-space-2 pr-space-2 text-right">
                    Attribution %
                  </th>
                  <th className="py-space-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-t border-border-light align-top"
                  >
                    <td className="py-space-2 pr-space-2">
                      <Select
                        aria-label="Loan"
                        value={row.loanId}
                        onChange={(e) =>
                          updateRow(row.id, { loanId: e.target.value })
                        }
                      >
                        <option value="">Select a loan…</option>
                        {loans.map((l) => (
                          <option key={l.loanId} value={l.loanId}>
                            {l.loanId.slice(0, 8)}…
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="py-space-2 pr-space-2">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step="any"
                        value={row.percent}
                        onChange={(e) =>
                          updateRow(row.id, { percent: e.target.value })
                        }
                        placeholder="0"
                        className="text-right"
                        aria-label="Attribution percent"
                      />
                    </td>
                    <td className="py-space-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(row.id)}
                        disabled={rows.length === 1}
                        className="text-gray-mid hover:text-status-error disabled:opacity-30"
                        aria-label="Remove attribution"
                      >
                        <Trash2 size={16} aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-space-3 flex items-center justify-between">
            <Button variant="secondary" size="sm" onClick={handleAddRow}>
              <Plus size={14} aria-hidden /> Add row
            </Button>
            <div
              className={cn(
                "text-small",
                percentValid ? "text-onyx" : "text-status-error font-semibold",
              )}
            >
              Total: {totalPercent.toFixed(2)}%
              {!percentValid && " (must equal 100.00)"}
            </div>
          </div>

          {loans.length === 0 && (
            <div className="mt-space-3 rounded-md border border-status-warning/30 bg-status-warning/10 p-space-3 text-small">
              This pool has no contributed loans yet. VFI inflows can only be
              recorded once the pool has contributed composition.
            </div>
          )}

          <div className="mt-space-4 flex justify-end">
            <Button
              size="sm"
              loading={preview.isPending}
              disabled={!step1Valid || loans.length === 0}
              onClick={handleRunPreview}
            >
              Preview split
            </Button>
          </div>
        </Card>
      )}

      {step === 2 && previewData && (
        <div className="flex flex-col gap-space-3">
          <Card>
            <h3 className="font-display font-bold text-h3 m-0">
              Layer totals
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-space-3 mt-space-3 text-small">
              <Item
                label="Gross VFI"
                value={formatCurrency(previewData.grossVfi)}
              />
              <Item
                label="Phoenix retained"
                value={formatCurrency(previewData.phoenixRetainedTotal)}
              />
              <Item
                label="Pool attributable"
                value={formatCurrency(previewData.poolAttributableTotal)}
              />
              <Item
                label="VMP admin fee (20%)"
                value={formatCurrency(previewData.vmpAdminFee)}
              />
              <Item
                label="Net VFI (to WF2)"
                value={formatCurrency(previewData.netVfi)}
              />
            </div>
          </Card>

          <Card>
            <h4 className="font-display font-bold text-onyx m-0">
              Layer 1 — per-loan split
            </h4>
            <div className="mt-space-3">
              <Table>
                <THead>
                  <TR>
                    <TH>Loan</TH>
                    <TH className="text-right">Attribution %</TH>
                    <TH className="text-right">Loan portion</TH>
                    <TH className="text-right">Phoenix retained</TH>
                    <TH className="text-right">Pool attributable</TH>
                  </TR>
                </THead>
                <TBody>
                  {previewData.perLoanSplits.map((s) => (
                    <TR key={s.loanId}>
                      <TD>{s.loanNumber}</TD>
                      <TD className="text-right">
                        {formatPct(s.attributionPercent)}
                      </TD>
                      <TD className="text-right">
                        {formatCurrency(s.loanPortion)}
                      </TD>
                      <TD className="text-right">
                        {formatCurrency(s.phoenixRetained)}
                      </TD>
                      <TD className="text-right">
                        {formatCurrency(s.poolAttributable)}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </Card>

          <Card>
            <h4 className="font-display font-bold text-onyx m-0">
              {previewData.wf2BlockedReason
                ? "WF2 disbursements"
                : `WF2 disbursements (${previewData.wf2Disbursements.length})`}
            </h4>
            {previewData.wf2BlockedReason ? (
              <div className="mt-space-3">
                <WaterfallBlockedNotice
                  reason={previewData.wf2BlockedReason}
                  subject="inflow"
                  scopeNote="The Layer 1 split above is unaffected, but the admin fee and net figures cannot be computed until the waterfall split resolves."
                />
              </div>
            ) : previewData.wf2Disbursements.length === 0 ? (
              <p className="text-small text-gray-mid italic mt-space-2 m-0">
                No disbursements computed for this inflow.
              </p>
            ) : (
              <div className="mt-space-3">
                <Table>
                  <THead>
                    <TR>
                      <TH>Priority</TH>
                      <TH>Recipient</TH>
                      <TH>Obligation</TH>
                      <TH className="text-right">Amount</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {previewData.wf2Disbursements.map((d, i) => (
                      <TR key={`${d.priorityPosition}-${i}`}>
                        <TD>P{d.priorityPosition}</TD>
                        <TD>
                          {d.recipientDisplayName ?? d.recipientResolution}
                        </TD>
                        <TD>
                          <code className="text-caption">
                            {d.obligationTypeCode}
                          </code>
                        </TD>
                        <TD className="text-right">
                          {formatCurrency(d.amount)}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            )}
          </Card>

          <div className="flex items-center justify-end gap-space-3">
            {previewData.wf2BlockedReason && (
              // Says why the button is dead, next to the button — the reason
              // itself is up in the WF2 card and may be scrolled off.
              <p className="m-0 text-small text-gray-mid">
                Blocked by the waterfall split above.
              </p>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setPreviewData(null);
                setStep(1);
              }}
            >
              Back
            </Button>
            <Button
              size="sm"
              loading={record.isPending}
              disabled={Boolean(previewData.wf2BlockedReason)}
              onClick={handleConfirm}
            >
              Confirm inflow
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Item({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-label uppercase tracking-label text-gray-mid">
        {label}
      </dt>
      <dd className="m-0 text-onyx font-semibold">{value}</dd>
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const labels = ["Inflow details", "Preview"];
  return (
    <ol className="flex items-center gap-space-2 m-0 p-0 list-none">
      {labels.map((label, i) => {
        const n = (i + 1) as Step;
        const done = step > n;
        const active = step === n;
        return (
          <li key={label} className="flex items-center gap-space-2">
            <span
              className={cn(
                "h-6 w-6 rounded-full flex items-center justify-center text-caption font-bold",
                done && "bg-status-success text-white-pure",
                active && "bg-gold text-onyx",
                !done && !active && "bg-white-warm text-gray-mid",
              )}
            >
              {done ? <Check size={12} aria-hidden /> : n}
            </span>
            <span
              className={cn(
                "text-caption font-display uppercase tracking-wide",
                active ? "text-onyx" : "text-gray-mid",
              )}
            >
              {label}
            </span>
            {i < labels.length - 1 && (
              <span className="text-gray-mid">→</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
